import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { toast } from "sonner";
import { Play, TrendingUp, TrendingDown, Sparkles, Clock, Check, X, Trash2, Filter } from "lucide-react";

function num(v, d = 2) {
  const n = parseFloat(v ?? 0);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

const DEFAULT_SYMBOLS = ["NVDA", "AAPL", "MSFT", "GOOGL", "META", "TSLA", "AMD", "AVGO", "ORCL", "AMZN"];

export default function Backtest() {
  const [symbolsText, setSymbolsText] = useState(DEFAULT_SYMBOLS.join(", "));
  const [lookbackDays, setLookbackDays] = useState(120);
  const [sideMode, setSideMode] = useState("both"); // "both" | "long_only" | "short_only"
  const [entryStrategy, setEntryStrategy] = useState(""); // "" = live default | "macd" | "slingshot"
  const [exitMode, setExitMode] = useState(""); // "" = live default | "fixed" | "trailing"
  const [loading, setLoading] = useState(false);
  const [loadingSec, setLoadingSec] = useState(0);
  const [result, setResult] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [optSec, setOptSec] = useState(0);
  const [optResult, setOptResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [fiRunning, setFiRunning] = useState(false);
  const [fiSec, setFiSec] = useState(0);
  const [fiResult, setFiResult] = useState(null);
  const [rcRunning, setRcRunning] = useState(false);
  const [rcSec, setRcSec] = useState(0);
  const [rcResult, setRcResult] = useState(null);
  const [soRunning, setSoRunning] = useState(false);
  const [soSec, setSoSec] = useState(0);
  const [soResult, setSoResult] = useState(null);

  const loadHistory = async () => {
    try {
      const r = await api.get("/strategy/optimizer-history");
      setHistory(r.data || []);
    } catch (err) { /* silent */ }
  };

  useEffect(() => { loadHistory(); }, []);

  const loadFromWatchlist = async () => {
    try {
      const r = await api.get("/strategy/watchlist");
      const syms = r.data?.symbols || [];
      if (syms.length === 0) {
        toast.error("Your bot watchlist is empty");
        return;
      }
      setSymbolsText(syms.join(", "));
      toast.success(`Loaded ${syms.length} symbols from your bot watchlist`);
    } catch (err) {
      toast.error("Could not load watchlist");
    }
  };

  const loadDiverseSample = () => {
    const sample = [
      // Calm large caps
      "AAPL", "MSFT", "GOOGL", "META", "JPM",
      // Medium volatility
      "AMZN", "NFLX", "V", "MA", "ADBE",
      // High volatility
      "TSLA", "NVDA", "COIN", "PLTR", "MSTR", "AMD", "ARM", "RIVN", "SOFI", "HOOD",
      // Sector leaders
      "UNH", "XOM", "CAT", "DIS", "NKE",
    ];
    setSymbolsText(sample.join(", "));
    toast.success(`Loaded 25-symbol diverse sample (calm + medium + volatile)`);
  };

  const symbols = () =>
    symbolsText.split(/[\s,]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);

  // Poll a job until done — bypasses Cloudflare's 100s timeout entirely.
  const pollJob = async (jobId, kind, onTick) => {
    const startedAt = Date.now();
    let networkErrors = 0;
    while (Date.now() - startedAt < 10 * 60 * 1000) { // 10-min max
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const r = await api.get(`/strategy/jobs/${jobId}`);
        networkErrors = 0; // reset on success
        if (r.data.status === "done") return r.data.result;
        if (r.data.status === "error") throw new Error(r.data.error || `${kind} failed`);
        if (onTick) onTick(Math.round((Date.now() - startedAt) / 1000));
      } catch (err) {
        // Re-throw server-reported errors (from r.data.error path above)
        if (err.message && !err.response) throw err;
        if (err.response?.status === 404) throw new Error("Job expired");
        networkErrors += 1;
        if (networkErrors >= 5) {
          throw new Error(`${kind} lost connection to server (5 consecutive network errors)`);
        }
        // transient network error — keep polling
      }
    }
    throw new Error(`${kind} timed out after 10 minutes`);
  };

  const run = async () => {
    setLoading(true);
    setLoadingSec(0);
    setResult(null);
    try {
      const r = await api.post("/strategy/backtest", {
        symbols: symbols(),
        lookback_days: parseInt(lookbackDays, 10),
        side_mode: sideMode,
        entry_strategy: entryStrategy || null,
        exit_mode: exitMode || null,
      });
      const data = await pollJob(r.data.job_id, "Backtest", setLoadingSec);
      setResult(data);
      toast.success(`Backtest done: ${data.total_trades} trades, ${data.win_rate}% win rate`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Backtest failed");
    } finally {
      setLoading(false);
    }
  };

  const runOptimize = async () => {
    setOptimizing(true);
    setOptSec(0);
    setOptResult(null);
    try {
      const r = await api.post("/strategy/optimize", { symbols: symbols(), lookback_days: parseInt(lookbackDays, 10), side_mode: sideMode });
      const data = await pollJob(r.data.job_id, "Optimizer", setOptSec);
      setOptResult(data);
      toast.success(`Optimizer done: ${data.valid_combos} combos tested`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Optimize failed");
    } finally {
      setOptimizing(false);
    }
  };

  const runFilterImpact = async () => {
    setFiRunning(true);
    setFiSec(0);
    setFiResult(null);
    try {
      const r = await api.post("/strategy/filter-impact", { symbols: symbols(), lookback_days: parseInt(lookbackDays, 10) });
      const data = await pollJob(r.data.job_id, "Filter impact", setFiSec);
      setFiResult(data);
      toast.success(`Filter impact done: ${data.scenarios?.length || 0} scenarios tested`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Filter impact failed");
    } finally {
      setFiRunning(false);
    }
  };

  const runRsiCompare = async () => {
    setRcRunning(true);
    setRcSec(0);
    setRcResult(null);
    try {
      const r = await api.post("/strategy/rsi-compare", { symbols: symbols(), lookback_days: parseInt(lookbackDays, 10), side_mode: sideMode });
      const data = await pollJob(r.data.job_id, "RSI compare", setRcSec);
      setRcResult(data);
      toast.success("RSI mode comparison done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "RSI compare failed");
    } finally {
      setRcRunning(false);
    }
  };

  const runShootout = async () => {
    setSoRunning(true);
    setSoSec(0);
    setSoResult(null);
    try {
      const r = await api.post("/strategy/shootout", { symbols: symbols(), lookback_days: parseInt(lookbackDays, 10), side_mode: sideMode });
      const data = await pollJob(r.data.job_id, "Shootout", setSoSec);
      setSoResult(data);
      toast.success("Strategy shootout done — 4 combos ranked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Shootout failed");
    } finally {
      setSoRunning(false);
    }
  };

  const applyShootoutCombo = async (s) => {
    try {
      await api.patch("/strategy/state", { entry_strategy: s.entry_strategy, exit_mode: s.exit_mode });
      toast.success(`Live bot set to: ${s.label}`);
      setSoResult((prev) => prev && ({
        ...prev,
        scenarios: prev.scenarios.map((x) => ({ ...x, is_live: x.id === s.id })),
      }));
    } catch (err) {
      toast.error("Failed to apply combo");
    }
  };

  const applyRsiMode = async (id, label) => {
    const payload = id === "off" ? { enable_rsi_filter: false } : { enable_rsi_filter: true, rsi_mode: id };
    try {
      await api.patch("/strategy/state", payload);
      toast.success(`Live bot RSI set to: ${label}`);
      setRcResult((prev) => prev && ({
        ...prev,
        live_mode: id,
        scenarios: prev.scenarios.map((s) => ({ ...s, is_live: s.id === id })),
      }));
    } catch (err) {
      toast.error("Failed to apply RSI mode");
    }
  };

  const applyBest = async () => {
    if (!optResult?.best) return;
    const p = optResult.best.params;
    try {
      await api.patch("/strategy/state", {
        stop_loss_pct: p.sl,
        take_profit_pct: p.tp,
        long_threshold: p.thr,
        short_threshold: -p.thr,
        enable_trend_filter: p.trend,
      });
      toast.success("Best params applied to live strategy");
    } catch (err) {
      toast.error("Failed to apply");
    }
  };

  const applyHistoryRun = async (runId) => {
    try {
      const r = await api.post(`/strategy/optimizer-history/${runId}/apply`);
      toast.success(`Applied → SL=${r.data.applied.sl}% TP=${r.data.applied.tp}% thr=${r.data.applied.thr}`);
      loadHistory();
    } catch (err) { toast.error("Apply failed"); }
  };

  const dismissHistoryRun = async (runId) => {
    try {
      await api.post(`/strategy/optimizer-history/${runId}/dismiss`);
      toast.success("Dismissed");
      loadHistory();
    } catch (err) { toast.error("Failed"); }
  };

  // --- Watchlist curator: remove losing symbols straight from backtest results ---
  const removeSymbolsFromWatchlist = async (symsToRemove, label) => {
    try {
      const r = await api.get("/strategy/watchlist");
      const current = r.data?.symbols || [];
      const toRemoveSet = new Set(symsToRemove.map((s) => s.toUpperCase()));
      const next = current.filter((s) => !toRemoveSet.has(s.toUpperCase()));
      if (next.length === current.length) {
        toast.message("None of these symbols were in your bot's watchlist");
        return;
      }
      await api.patch("/strategy/state", { watchlist: next });
      toast.success(`${label}: removed ${current.length - next.length} symbol${current.length - next.length === 1 ? "" : "s"} from bot watchlist`);
    } catch (err) {
      toast.error("Watchlist update failed");
    }
  };

  const removeOne = (sym) => removeSymbolsFromWatchlist([sym], `❌ ${sym}`);

  const removeAllLosers = () => {
    if (!result?.symbol_stats) return;
    const losers = Object.entries(result.symbol_stats)
      .filter(([, s]) => s.total_pnl_pct < 0 && s.trades >= 3)
      .map(([sym]) => sym);
    if (losers.length === 0) {
      toast.message("No losers with ≥3 trades to remove");
      return;
    }
    if (!confirm(`Remove ${losers.length} losing symbols from bot watchlist?\n\n${losers.join(", ")}`)) return;
    removeSymbolsFromWatchlist(losers, "🚫 Bulk removal");
  };

  const keepOnlyWinners = () => {
    if (!result?.symbol_stats) return;
    const nonWinners = Object.entries(result.symbol_stats)
      .filter(([, s]) => s.total_pnl_pct <= 0 || s.win_rate < 50)
      .map(([sym]) => sym);
    if (nonWinners.length === 0) {
      toast.message("All tested symbols are winners — nothing to trim");
      return;
    }
    if (!confirm(`Aggressive trim: remove ${nonWinners.length} symbols (any with P&L ≤ 0% OR win-rate < 50%)?\n\n${nonWinners.join(", ")}`)) return;
    removeSymbolsFromWatchlist(nonWinners, "🏆 Winners-only trim");
  };

  const triggerNow = async () => {
    if (!confirm("Run the weekly optimizer right now? Takes ~30s.")) return;
    try {
      toast.message("Triggering optimizer...");
      await api.post("/strategy/optimizer-trigger");
      toast.success("Optimizer run complete — check Pending Review below");
      loadHistory();
    } catch (err) { toast.error("Trigger failed"); }
  };

  const pending = history.filter((h) => h.status === "pending");

  const positiveReturn = (result?.portfolio_return_pct || 0) >= 0;

  return (
    <div className="p-6 space-y-6" data-testid="backtest-page">
      <div>
        <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">HISTORICAL SIMULATION · MACD 12·26·9 · 1H</div>
        <h1 className="text-4xl font-black mt-2">Backtester</h1>
        <p className="text-[#A1A1AA] text-sm mt-1">
          Replay your live strategy parameters on past hourly bars from Alpaca. Uses your current{" "}
          <span className="mono text-white">stop loss</span>, <span className="mono text-white">take profit</span>,{" "}
          <span className="mono text-white">MACD thresholds</span> and <span className="mono text-white">allocation %</span>.
        </p>
      </div>

      {/* Run panel */}
      <div className="panel" data-testid="backtest-controls">
        <div className="px-4 py-3 border-b border-[#27272A] mono text-[11px] tracking-[0.22em]">
          CONFIGURATION
        </div>
        <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-1">
              <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">
                SYMBOLS (COMMA SEPARATED) · {symbols().length} loaded
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={loadDiverseSample}
                  data-testid="bt-load-sample-btn"
                  className="mono text-[10px] tracking-widest text-[#A1A1AA] hover:text-white border border-[#27272A] hover:border-[#52525B] px-2 py-0.5"
                  title="Load a 25-symbol diverse mix (calm + medium + volatile) — ideal for testing ATR vs % stops"
                >
                  + DIVERSE 25
                </button>
                <button
                  type="button"
                  onClick={loadFromWatchlist}
                  data-testid="bt-load-watchlist-btn"
                  className="mono text-[10px] tracking-widest text-[#A1A1AA] hover:text-white border border-[#27272A] hover:border-[#52525B] px-2 py-0.5"
                  title="Load all symbols from your bot's saved watchlist"
                >
                  + MY WATCHLIST
                </button>
              </div>
            </div>
            <input
              value={symbolsText}
              onChange={(e) => setSymbolsText(e.target.value)}
              data-testid="bt-symbols-input"
              className="w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
            />
          </div>
          <div>
            <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">LOOKBACK DAYS</label>
            <input
              type="number"
              min="7"
              max="365"
              value={lookbackDays}
              onChange={(e) => setLookbackDays(e.target.value)}
              data-testid="bt-lookback-input"
              className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm"
            />
          </div>
          <div className="lg:col-span-3">
            <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">TRADE SIDE</label>
            <div className="mt-1 grid grid-cols-3 gap-0 border border-[#27272A]">
              {[
                { v: "both", label: "LONG + SHORT" },
                { v: "long_only", label: "LONG ONLY" },
                { v: "short_only", label: "SHORT ONLY" },
              ].map((opt, idx) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setSideMode(opt.v)}
                  data-testid={`bt-side-${opt.v}`}
                  className={`mono text-[10px] tracking-[0.22em] py-2.5 transition-colors ${
                    sideMode === opt.v
                      ? "bg-white text-black font-bold"
                      : "bg-[#09090B] text-[#71717A] hover:text-white"
                  } ${idx < 2 ? "border-r border-[#27272A]" : ""}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="mono text-[10px] text-[#71717A] mt-1">
              Overrides live "shorts enabled" setting for this run only. Live bot unchanged.
            </div>
          </div>
          <div className="lg:col-span-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">ENTRY STRATEGY (BACKTEST ONLY)</label>
              <div className="mt-1 grid grid-cols-4 gap-0 border border-[#27272A]">
                {[
                  { v: "", label: "LIVE SETTING" },
                  { v: "macd", label: "MACD" },
                  { v: "slingshot", label: "SLINGSHOT" },
                  { v: "orderblock", label: "ORDER BLOCK" },
                ].map((opt, idx) => (
                  <button
                    key={opt.v || "live"}
                    type="button"
                    onClick={() => setEntryStrategy(opt.v)}
                    data-testid={`bt-entry-${opt.v || "live"}`}
                    className={`mono text-[10px] tracking-[0.18em] py-2 transition-colors ${
                      entryStrategy === opt.v
                        ? "bg-white text-black font-bold"
                        : "bg-[#09090B] text-[#71717A] hover:text-white"
                    } ${idx < 3 ? "border-r border-[#27272A]" : ""}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mono text-[10px] tracking-[0.22em] text-[#71717A]">EXIT MODE (BACKTEST ONLY)</label>
              <div className="mt-1 grid grid-cols-3 gap-0 border border-[#27272A]">
                {[
                  { v: "", label: "LIVE SETTING" },
                  { v: "fixed", label: "FIXED TP/SL" },
                  { v: "trailing", label: "TRAILING" },
                ].map((opt, idx) => (
                  <button
                    key={opt.v || "live"}
                    type="button"
                    onClick={() => setExitMode(opt.v)}
                    data-testid={`bt-exit-${opt.v || "live"}`}
                    className={`mono text-[10px] tracking-[0.18em] py-2 transition-colors ${
                      exitMode === opt.v
                        ? "bg-white text-black font-bold"
                        : "bg-[#09090B] text-[#71717A] hover:text-white"
                    } ${idx < 2 ? "border-r border-[#27272A]" : ""}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2 mono text-[10px] text-[#71717A] -mt-2">
              Test MACD vs SlingShot entries and Fixed vs Trailing exits WITHOUT touching the live bot — applies to RUN BACKTEST only.
            </div>
          </div>
          <div className="lg:col-span-3 grid grid-cols-1 lg:grid-cols-4 gap-3">
            <button
              onClick={run}
              disabled={loading || optimizing || fiRunning || rcRunning || soRunning}
              data-testid="bt-run-btn"
              className="bg-[#00E676] text-black mono text-xs tracking-[0.22em] font-bold py-3 hover:bg-[#00C964] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Play size={14} />
              {loading ? `RUNNING BACKTEST...` : `RUN BACKTEST →`}
            </button>
            <button
              onClick={runOptimize}
              disabled={loading || optimizing || fiRunning || rcRunning || soRunning}
              data-testid="bt-optimize-btn"
              className="bg-white text-black mono text-xs tracking-[0.22em] font-bold py-3 hover:bg-[#D4D4D8] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Sparkles size={14} />
              {optimizing ? `OPTIMIZING (54 combos)...` : `OPTIMIZE PARAMS →`}
            </button>
            <button
              onClick={runFilterImpact}
              disabled={loading || optimizing || fiRunning || rcRunning || soRunning}
              data-testid="bt-filter-impact-btn"
              title="Test each enabled filter ON vs OFF to see which one is starving the bot of trades"
              className="bg-[#EAB308] text-black mono text-xs tracking-[0.22em] font-bold py-3 hover:bg-[#CA9E06] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Filter size={14} />
              {fiRunning ? `ANALYZING FILTERS...` : `FILTER IMPACT →`}
            </button>
            <button
              onClick={runRsiCompare}
              disabled={loading || optimizing || fiRunning || rcRunning || soRunning}
              data-testid="bt-rsi-compare-btn"
              title="Backtest RSI OFF vs AVOID EXTREMES vs MOMENTUM side by side on identical data"
              className="bg-[#FFD60A] text-black mono text-xs tracking-[0.22em] font-bold py-3 hover:bg-[#E6C009] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              🌡 {rcRunning ? `COMPARING RSI...` : `COMPARE RSI MODES →`}
            </button>
          </div>
          <div className="lg:col-span-3">
            <button
              onClick={runShootout}
              disabled={loading || optimizing || fiRunning || rcRunning || soRunning}
              data-testid="bt-shootout-btn"
              title="Backtest all 4 combos — MACD/SlingShot × Fixed/Trailing — ranked in one table"
              className="w-full bg-[#FF9F0A] text-black mono text-xs tracking-[0.22em] font-bold py-3 hover:bg-[#E08D08] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              ⚔ {soRunning ? `RUNNING SHOOTOUT (6 COMBOS)...` : `STRATEGY SHOOTOUT — RANK ALL 6 COMBOS →`}
            </button>
          </div>
        </div>
      </div>

      {/* Weekly Optimizer Auto-Schedule */}
      <div className="panel" data-testid="weekly-optimizer-panel">
        <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
          <div className="flex items-center gap-2 mono text-[11px] tracking-[0.22em]">
            <Clock size={13} /> WEEKLY AUTO-OPTIMIZER · SUNDAYS 22:00 UTC
          </div>
          <button
            onClick={triggerNow}
            data-testid="trigger-weekly-btn"
            className="border border-[#27272A] hover:border-[#52525B] px-3 py-1 mono text-[10px] tracking-widest"
          >
            RUN NOW (TEST) →
          </button>
        </div>

        {pending.length > 0 ? (
          <div className="p-4 space-y-3" data-testid="pending-runs">
            {pending.map((run) => {
              const b = run.best;
              const cp = run.current_params || {};
              return (
                <div key={run.run_id} className="border border-[#00E676]/40 bg-[#00E676]/5 p-4" data-testid={`pending-${run.run_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="mono text-[10px] tracking-widest gain">⚡ PENDING REVIEW</div>
                      <div className="mono text-xs text-[#A1A1AA] mt-1">
                        {new Date(run.created_at).toLocaleString()} · {run.symbols?.length || 0} symbols · {run.lookback_days}d lookback
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => applyHistoryRun(run.run_id)}
                        data-testid="apply-pending-btn"
                        className="bg-[#00E676] text-black px-4 py-2 mono text-[10px] tracking-widest font-bold hover:bg-[#00C964] flex items-center gap-1"
                      >
                        <Check size={12} /> APPLY
                      </button>
                      <button
                        onClick={() => dismissHistoryRun(run.run_id)}
                        data-testid="dismiss-pending-btn"
                        className="bg-[#27272A] text-[#A1A1AA] px-4 py-2 mono text-[10px] tracking-widest hover:text-white flex items-center gap-1"
                      >
                        <X size={12} /> DISMISS
                      </button>
                    </div>
                  </div>

                  {b ? (
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-[#27272A] border border-[#27272A]">
                      {[
                        { l: "RETURN", v: `${b.portfolio_return_pct >= 0 ? "+" : ""}${num(b.portfolio_return_pct)}%`, c: b.portfolio_return_pct >= 0 ? "gain" : "loss" },
                        { l: "WIN RATE", v: `${num(b.win_rate, 1)}%` },
                        { l: "PROFIT FACTOR", v: num(b.profit_factor) },
                        { l: "MAX DD", v: `-${num(b.max_drawdown_pct)}%`, c: "loss" },
                        { l: "TRADES", v: num(b.trades, 0) },
                      ].map((s) => (
                        <div key={s.l} className="bg-[#141415] p-3">
                          <div className="mono text-[10px] tracking-widest text-[#71717A]">{s.l}</div>
                          <div className={`mono text-base mt-1 ${s.c || ""}`}>{s.v}</div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {b && (
                    <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3 mono text-xs">
                      <div className="border border-[#27272A] p-3">
                        <div className="text-[#71717A] tracking-widest text-[10px] mb-2">CURRENT LIVE PARAMS</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>SL: <span className="text-white">{num(cp.stop_loss_pct, 1)}%</span></div>
                          <div>TP: <span className="text-white">{num(cp.take_profit_pct, 1)}%</span></div>
                          <div>THR: <span className="text-white">{num(cp.long_threshold, 2)}</span></div>
                          <div>TREND: <span className="text-white">{cp.enable_trend_filter ? "ON" : "OFF"}</span></div>
                        </div>
                      </div>
                      <div className="border border-[#00E676] p-3">
                        <div className="gain tracking-widest text-[10px] mb-2">PROPOSED PARAMS</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>SL: <span className="text-white">{b.params.sl}%</span></div>
                          <div>TP: <span className="text-white">{b.params.tp}%</span></div>
                          <div>THR: <span className="text-white">{b.params.thr}</span></div>
                          <div>TREND: <span className="text-white">{b.params.trend ? "ON" : "OFF"}</span></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-6 text-center text-[#71717A] mono text-xs tracking-widest">
            NO PENDING RECOMMENDATIONS — NEXT AUTO-RUN: NEXT SUNDAY 22:00 UTC
          </div>
        )}

        {history.filter((h) => h.status !== "pending").length > 0 && (
          <div className="border-t border-[#27272A]">
            <div className="px-4 py-3 mono text-[10px] tracking-widest text-[#71717A]">
              HISTORY · {history.filter((h) => h.status !== "pending").length}
            </div>
            <div className="max-h-60 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#141415] sticky top-0">
                  <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                    <th className="text-left px-3 py-2 font-normal">WHEN</th>
                    <th className="text-left px-3 py-2 font-normal">STATUS</th>
                    <th className="text-right px-3 py-2 font-normal">SL</th>
                    <th className="text-right px-3 py-2 font-normal">TP</th>
                    <th className="text-right px-3 py-2 font-normal">THR</th>
                    <th className="text-left px-3 py-2 font-normal">TREND</th>
                    <th className="text-right px-3 py-2 font-normal">RETURN</th>
                    <th className="text-right px-3 py-2 font-normal">WIN%</th>
                  </tr>
                </thead>
                <tbody>
                  {history.filter((h) => h.status !== "pending").map((h) => {
                    const p = h.best?.params || {};
                    const b = h.best || {};
                    return (
                      <tr key={h.run_id} className="border-b border-[#27272A]">
                        <td className="px-3 py-2 mono text-xs text-[#A1A1AA]">{new Date(h.created_at).toLocaleDateString()}</td>
                        <td className={`px-3 py-2 mono text-xs ${h.status === "applied" ? "gain" : "text-[#71717A]"}`}>
                          {h.status?.toUpperCase()}
                        </td>
                        <td className="px-3 py-2 mono text-right">{p.sl ?? "—"}</td>
                        <td className="px-3 py-2 mono text-right">{p.tp ?? "—"}</td>
                        <td className="px-3 py-2 mono text-right">{p.thr ?? "—"}</td>
                        <td className="px-3 py-2 mono">{p.trend ? "ON" : "off"}</td>
                        <td className={`px-3 py-2 mono text-right ${(b.portfolio_return_pct || 0) >= 0 ? "gain" : "loss"}`}>
                          {(b.portfolio_return_pct || 0) >= 0 ? "+" : ""}{num(b.portfolio_return_pct)}%
                        </td>
                        <td className="px-3 py-2 mono text-right">{num(b.win_rate, 1)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="panel p-12 text-center mono text-xs text-[#71717A] tracking-widest" data-testid="bt-loading">
          SIMULATING TRADES ACROSS {symbols().length} SYMBOLS · {lookbackDays} DAYS OF HOURLY BARS
          <div className="mt-2 gain">ELAPSED · {loadingSec}s</div>
        </div>
      )}

      {optimizing && (
        <div className="panel p-12 text-center mono text-xs text-[#71717A] tracking-widest" data-testid="opt-loading">
          SWEEPING 54 PARAMETER COMBOS · THIS TAKES 30-90 SECONDS
          <div className="mt-2 gain">ELAPSED · {optSec}s {optSec >= 120 ? "· (taking longer than expected — please wait)" : ""}</div>
        </div>
      )}

      {fiRunning && (
        <div className="panel p-12 text-center mono text-xs text-[#71717A] tracking-widest" data-testid="fi-loading">
          TESTING EACH FILTER ON vs OFF ACROSS {symbols().length} SYMBOLS · {lookbackDays} DAYS
          <div className="mt-2 gain">ELAPSED · {fiSec}s</div>
        </div>
      )}

      {fiResult && (
        <div className="panel" data-testid="filter-impact-results">
          <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between flex-wrap gap-2">
            <div className="mono text-[11px] tracking-[0.22em]">
              FILTER IMPACT · {fiResult.symbols_count} SYMBOLS · {fiResult.lookback_days}D · {fiResult.bar_timeframe}
            </div>
            <div className="mono text-[10px] text-[#71717A]">
              {fiResult.enabled_filters?.length || 0} FILTERS CURRENTLY ON
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#141415]">
                <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                  <th className="text-left px-4 py-2 font-normal">SCENARIO</th>
                  <th className="text-right px-3 py-2 font-normal">TRADES</th>
                  <th className="text-right px-3 py-2 font-normal">TRADES/WK</th>
                  <th className="text-right px-3 py-2 font-normal">Δ TRADES</th>
                  <th className="text-right px-3 py-2 font-normal">WIN %</th>
                  <th className="text-right px-3 py-2 font-normal">RETURN</th>
                  <th className="text-right px-3 py-2 font-normal">PF</th>
                  <th className="text-right px-4 py-2 font-normal">MAX DD</th>
                </tr>
              </thead>
              <tbody>
                {(fiResult.scenarios || []).map((s) => {
                  const isCurrent = s.id === "current";
                  const isAllOff = s.id === "all_off";
                  return (
                    <tr
                      key={s.id}
                      className={`border-b border-[#27272A] ${isCurrent ? "bg-white/5" : ""} ${isAllOff ? "bg-[#EAB308]/5" : ""}`}
                      data-testid={`fi-row-${s.id}`}
                    >
                      <td className={`px-4 py-2.5 mono text-xs ${isCurrent ? "text-white font-bold" : isAllOff ? "text-[#EAB308]" : "text-[#A1A1AA]"}`}>
                        {s.label}
                      </td>
                      <td className="px-3 py-2.5 mono text-right">{s.trades}</td>
                      <td className="px-3 py-2.5 mono text-right font-bold">{num(s.trades_per_week, 1)}</td>
                      <td className={`px-3 py-2.5 mono text-right ${s.extra_trades_vs_current > 0 ? "gain" : "text-[#71717A]"}`}>
                        {s.extra_trades_vs_current > 0 ? `+${s.extra_trades_vs_current}` : s.extra_trades_vs_current}
                      </td>
                      <td className="px-3 py-2.5 mono text-right">{num(s.win_rate, 1)}%</td>
                      <td className={`px-3 py-2.5 mono text-right ${s.portfolio_return_pct >= 0 ? "gain" : "loss"}`}>
                        {s.portfolio_return_pct >= 0 ? "+" : ""}{num(s.portfolio_return_pct)}%
                      </td>
                      <td className="px-3 py-2.5 mono text-right">{num(s.profit_factor)}</td>
                      <td className="px-4 py-2.5 mono text-right loss">-{num(s.max_drawdown_pct)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 mono text-[10px] text-[#71717A] border-t border-[#27272A]">
            Each "WITHOUT" row switches OFF exactly one filter, keeping everything else at your live config.
            A big jump in TRADES/WK with a similar WIN % means that filter is starving the bot for little benefit.
            Turn filters off on the Strategy page.
          </div>
        </div>
      )}

      {rcRunning && (
        <div className="panel p-12 text-center mono text-xs text-[#71717A] tracking-widest" data-testid="rc-loading">
          BACKTESTING RSI OFF vs AVOID EXTREMES vs MOMENTUM · {symbols().length} SYMBOLS · {lookbackDays} DAYS
          <div className="mt-2 gain">ELAPSED · {rcSec}s</div>
        </div>
      )}

      {soRunning && (
        <div className="panel p-12 text-center mono text-xs text-[#71717A] tracking-widest" data-testid="so-loading">
          ⚔ SHOOTOUT · MACD/SLINGSHOT/ORDER BLOCK × FIXED/TRAILING · {symbols().length} SYMBOLS · {lookbackDays} DAYS
          <div className="mt-2 gain">ELAPSED · {soSec}s</div>
        </div>
      )}

      {soResult && (
        <div className="panel" data-testid="shootout-results">
          <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between flex-wrap gap-2">
            <div className="mono text-[11px] tracking-[0.22em]">
              ⚔ STRATEGY SHOOTOUT · {soResult.symbols_count} SYMBOLS · {soResult.lookback_days}D · {soResult.bar_timeframe} · TRAIL {num(soResult.trail_pct, 1)}%
            </div>
            {(() => {
              const best = (soResult.scenarios || []).find((s) => s.is_best);
              if (!best) return null;
              return best.is_live ? (
                <span className="mono text-[10px] tracking-widest gain border border-[#00E676]/40 px-3 py-1" data-testid="so-best-is-live">
                  ✓ BEST COMBO ALREADY LIVE
                </span>
              ) : (
                <button
                  onClick={() => applyShootoutCombo(best)}
                  data-testid="so-apply-best-btn"
                  className="bg-[#00E676] text-black mono text-[10px] tracking-widest font-bold px-3 py-1 hover:bg-[#00C964]"
                >
                  ✓ APPLY WINNING COMBO
                </button>
              );
            })()}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#141415]">
                <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                  <th className="text-right px-3 py-2 font-normal">#</th>
                  <th className="text-left px-4 py-2 font-normal">COMBO</th>
                  <th className="text-right px-3 py-2 font-normal">TRADES</th>
                  <th className="text-right px-3 py-2 font-normal">TRADES/WK</th>
                  <th className="text-right px-3 py-2 font-normal">WIN %</th>
                  <th className="text-right px-3 py-2 font-normal">RETURN</th>
                  <th className="text-right px-3 py-2 font-normal">PF</th>
                  <th className="text-right px-3 py-2 font-normal">MAX DD</th>
                  <th className="text-right px-4 py-2 font-normal w-20"></th>
                </tr>
              </thead>
              <tbody>
                {(soResult.scenarios || []).map((s, i) => (
                  <tr
                    key={s.id}
                    className={`border-b border-[#27272A] ${s.is_best ? "bg-[#00E676]/5" : s.is_live ? "bg-[#FF9F0A]/5" : ""}`}
                    data-testid={`so-row-${s.id}`}
                  >
                    <td className="px-3 py-2.5 mono text-right text-[#71717A]">{i + 1}</td>
                    <td className={`px-4 py-2.5 mono text-xs ${s.is_live ? "text-[#FF9F0A] font-bold" : "text-[#A1A1AA]"}`}>
                      {s.is_best ? <span className="gain">★ </span> : null}{s.label}{s.is_live ? " · LIVE" : ""}
                    </td>
                    <td className="px-3 py-2.5 mono text-right">{s.trades}</td>
                    <td className="px-3 py-2.5 mono text-right font-bold">{num(s.trades_per_week, 1)}</td>
                    <td className="px-3 py-2.5 mono text-right">{num(s.win_rate, 1)}%</td>
                    <td className={`px-3 py-2.5 mono text-right ${s.portfolio_return_pct >= 0 ? "gain" : "loss"}`}>
                      {s.portfolio_return_pct >= 0 ? "+" : ""}{num(s.portfolio_return_pct)}%
                    </td>
                    <td className="px-3 py-2.5 mono text-right">{num(s.profit_factor)}</td>
                    <td className="px-3 py-2.5 mono text-right loss">-{num(s.max_drawdown_pct)}%</td>
                    <td className="px-4 py-2.5 text-right">
                      {s.is_live ? (
                        <span className="mono text-[9px] tracking-widest text-[#FF9F0A]">LIVE</span>
                      ) : (
                        <button
                          onClick={() => applyShootoutCombo(s)}
                          data-testid={`so-apply-${s.id}`}
                          title="Set the live bot to this entry + exit combo"
                          className="border border-[#27272A] text-[#A1A1AA] hover:text-black hover:bg-[#00E676] hover:border-[#00E676] px-2 py-1 mono text-[9px] tracking-widest font-bold transition-colors"
                        >
                          APPLY
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 mono text-[10px] text-[#71717A] border-t border-[#27272A]">
            Identical data, filters and sizing — the only variables are the entry signal and exit style.
            Ranked by return minus half the drawdown (needs ≥3 trades). ★ = best · tap APPLY to switch the live bot.
          </div>
        </div>
      )}

      {rcResult && (
        <div className="panel" data-testid="rsi-compare-results">
          <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between flex-wrap gap-2">
            <div className="mono text-[11px] tracking-[0.22em]">
              🌡 RSI MODE COMPARISON · {rcResult.symbols_count} SYMBOLS · {rcResult.lookback_days}D · {rcResult.bar_timeframe}
            </div>
            <div className="flex items-center gap-3">
              <div className="mono text-[10px] text-[#71717A]">
                THRESHOLDS · {num(rcResult.rsi_long_max, 0)} / {num(rcResult.rsi_short_min, 0)}
              </div>
              {(() => {
                const best = (rcResult.scenarios || []).find((s) => s.is_best);
                if (!best) return null;
                return best.is_live ? (
                  <span className="mono text-[10px] tracking-widest gain border border-[#00E676]/40 px-3 py-1" data-testid="rc-best-is-live">
                    ✓ BEST MODE ALREADY LIVE
                  </span>
                ) : (
                  <button
                    onClick={() => applyRsiMode(best.id, best.label)}
                    data-testid="rc-apply-best-btn"
                    className="bg-[#00E676] text-black mono text-[10px] tracking-widest font-bold px-3 py-1 hover:bg-[#00C964]"
                  >
                    ✓ APPLY WINNING MODE
                  </button>
                );
              })()}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#141415]">
                <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                  <th className="text-left px-4 py-2 font-normal">RSI MODE</th>
                  <th className="text-right px-3 py-2 font-normal">TRADES</th>
                  <th className="text-right px-3 py-2 font-normal">TRADES/WK</th>
                  <th className="text-right px-3 py-2 font-normal">WIN %</th>
                  <th className="text-right px-3 py-2 font-normal">RETURN</th>
                  <th className="text-right px-3 py-2 font-normal">PF</th>
                  <th className="text-right px-3 py-2 font-normal">MAX DD</th>
                  <th className="text-right px-4 py-2 font-normal w-20"></th>
                </tr>
              </thead>
              <tbody>
                {(rcResult.scenarios || []).map((s) => (
                  <tr
                    key={s.id}
                    className={`border-b border-[#27272A] ${s.is_best ? "bg-[#00E676]/5" : s.is_live ? "bg-[#FFD60A]/5" : ""}`}
                    data-testid={`rc-row-${s.id}`}
                  >
                    <td className={`px-4 py-2.5 mono text-xs ${s.is_live ? "text-[#FFD60A] font-bold" : "text-[#A1A1AA]"}`}>
                      {s.is_best ? <span className="gain">★ </span> : null}{s.label}{s.is_live ? " · LIVE" : ""}
                    </td>
                    <td className="px-3 py-2.5 mono text-right">{s.trades}</td>
                    <td className="px-3 py-2.5 mono text-right font-bold">{num(s.trades_per_week, 1)}</td>
                    <td className="px-3 py-2.5 mono text-right">{num(s.win_rate, 1)}%</td>
                    <td className={`px-3 py-2.5 mono text-right ${s.portfolio_return_pct >= 0 ? "gain" : "loss"}`}>
                      {s.portfolio_return_pct >= 0 ? "+" : ""}{num(s.portfolio_return_pct)}%
                    </td>
                    <td className="px-3 py-2.5 mono text-right">{num(s.profit_factor)}</td>
                    <td className="px-3 py-2.5 mono text-right loss">-{num(s.max_drawdown_pct)}%</td>
                    <td className="px-4 py-2.5 text-right">
                      {s.is_live ? (
                        <span className="mono text-[9px] tracking-widest text-[#FFD60A]">LIVE</span>
                      ) : (
                        <button
                          onClick={() => applyRsiMode(s.id, s.label)}
                          data-testid={`rc-apply-${s.id}`}
                          title="Set the live bot to this RSI mode"
                          className="border border-[#27272A] text-[#A1A1AA] hover:text-black hover:bg-[#00E676] hover:border-[#00E676] px-2 py-1 mono text-[9px] tracking-widest font-bold transition-colors"
                        >
                          APPLY
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 mono text-[10px] text-[#71717A] border-t border-[#27272A]">
            Identical data, identical filters — the ONLY variable is the RSI treatment. ★ = best (return minus half the drawdown, needs ≥3 trades).
            The highlighted row is your live setting — tap APPLY to switch the live bot instantly.
          </div>
        </div>
      )}


      {optResult && (
        <div className="panel" data-testid="optimizer-results">
          <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
            <div className="mono text-[11px] tracking-[0.22em]">
              OPTIMIZER · TOP {optResult.results.length} OF {optResult.total_combos} COMBOS
            </div>
            <button
              onClick={applyBest}
              data-testid="apply-best-btn"
              className="bg-[#00E676] text-black mono text-[10px] tracking-widest font-bold px-3 py-1 hover:bg-[#00C964]"
            >
              ✓ APPLY BEST PARAMS TO LIVE BOT
            </button>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#141415]">
                <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                  <th className="text-right px-3 py-2 font-normal">#</th>
                  <th className="text-right px-3 py-2 font-normal">SL%</th>
                  <th className="text-right px-3 py-2 font-normal">TP%</th>
                  <th className="text-right px-3 py-2 font-normal">THR</th>
                  <th className="text-left px-3 py-2 font-normal">TREND</th>
                  <th className="text-right px-3 py-2 font-normal">TRADES</th>
                  <th className="text-right px-3 py-2 font-normal">WIN %</th>
                  <th className="text-right px-3 py-2 font-normal">RETURN</th>
                  <th className="text-right px-3 py-2 font-normal">PF</th>
                  <th className="text-right px-3 py-2 font-normal">MAX DD</th>
                </tr>
              </thead>
              <tbody>
                {optResult.results.map((c, i) => (
                  <tr key={i} className={`border-b border-[#27272A] ${i === 0 ? "bg-[#00E676]/5" : ""}`} data-testid={`opt-row-${i}`}>
                    <td className="px-3 py-2 mono text-[#71717A]">{i + 1}</td>
                    <td className="px-3 py-2 mono text-right">{c.params.sl}</td>
                    <td className="px-3 py-2 mono text-right">{c.params.tp}</td>
                    <td className="px-3 py-2 mono text-right">{c.params.thr}</td>
                    <td className={`px-3 py-2 mono ${c.params.trend ? "gain" : "text-[#71717A]"}`}>{c.params.trend ? "ON" : "off"}</td>
                    <td className="px-3 py-2 mono text-right">{c.trades}</td>
                    <td className="px-3 py-2 mono text-right">{num(c.win_rate, 1)}%</td>
                    <td className={`px-3 py-2 mono text-right ${c.portfolio_return_pct >= 0 ? "gain" : "loss"}`}>
                      {c.portfolio_return_pct >= 0 ? "+" : ""}{num(c.portfolio_return_pct)}%
                    </td>
                    <td className="px-3 py-2 mono text-right">{num(c.profit_factor)}</td>
                    <td className="px-3 py-2 mono text-right loss">-{num(c.max_drawdown_pct)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <>
          {/* Top-line stats */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-[#27272A] border border-[#27272A]" data-testid="bt-summary">
            {[
              { label: "PORTFOLIO RETURN", value: `${positiveReturn ? "+" : ""}${num(result.portfolio_return_pct)}%`, color: positiveReturn ? "gain" : "loss" },
              { label: "WIN RATE", value: `${num(result.win_rate, 1)}%` },
              { label: "TRADES", value: num(result.total_trades, 0) },
              { label: "PROFIT FACTOR", value: num(result.profit_factor) },
              { label: "MAX DRAWDOWN", value: `-${num(result.max_drawdown_pct)}%`, color: "loss" },
              { label: "AVG WIN / LOSS", value: `${num(result.avg_win_pct)}% / ${num(result.avg_loss_pct)}%` },
            ].map((s) => (
              <div key={s.label} className="bg-[#141415] p-4">
                <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">{s.label}</div>
                <div className={`mono text-xl mt-2 ${s.color || ""}`}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Equity curve */}
          <div className="panel" data-testid="bt-equity-panel">
            <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
              <div className="mono text-[11px] tracking-[0.22em]">EQUITY CURVE · PORTFOLIO % RETURN</div>
              <div className="mono text-[10px] text-[#71717A]">{result.equity_curve.length} TRADES PLOTTED</div>
            </div>
            <div className="h-72 px-2">
              {result.equity_curve.length === 0 ? (
                <div className="h-full flex items-center justify-center text-[#71717A] mono text-xs">
                  NO TRADES IN PERIOD — ADJUST THRESHOLDS OR EXPAND WATCHLIST
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={result.equity_curve} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                    <XAxis
                      dataKey="t"
                      stroke="#71717A"
                      tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#71717A" }}
                      tickFormatter={(t) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      minTickGap={40}
                    />
                    <YAxis
                      stroke="#71717A"
                      tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#71717A" }}
                      width={60}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip
                      contentStyle={{ background: "#141415", border: "1px solid #27272A", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 }}
                      formatter={(value, name, item) => [`${value}% (${item.payload.symbol} ${item.payload.trade_pnl >= 0 ? "+" : ""}${item.payload.trade_pnl}%)`, "EQUITY"]}
                      labelFormatter={(t) => new Date(t).toLocaleString()}
                    />
                    <ReferenceLine y={0} stroke="#52525B" strokeDasharray="2 2" />
                    <Line
                      type="monotone"
                      dataKey="equity"
                      stroke={positiveReturn ? "#00E676" : "#FF3B30"}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Symbol breakdown */}
            <div className="panel" data-testid="bt-symbol-breakdown">
              <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between gap-2 flex-wrap">
                <div className="mono text-[11px] tracking-[0.22em]">
                  PER-SYMBOL · {Object.keys(result.symbol_stats || {}).length}
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={removeAllLosers}
                    data-testid="bt-remove-losers-btn"
                    title="Remove every symbol with negative P&L and ≥3 trades from your bot's watchlist"
                    className="mono text-[9px] tracking-widest text-[#FF3B30] hover:text-white hover:bg-[#FF3B30]/20 border border-[#FF3B30]/40 hover:border-[#FF3B30] px-2 py-1 flex items-center gap-1"
                  >
                    🚫 KILL LOSERS
                  </button>
                  <button
                    onClick={keepOnlyWinners}
                    data-testid="bt-keep-winners-btn"
                    title="Aggressive trim: keep only profitable symbols with ≥50% win rate"
                    className="mono text-[9px] tracking-widest text-[#00E676] hover:text-black hover:bg-[#00E676] border border-[#00E676]/40 hover:border-[#00E676] px-2 py-1 flex items-center gap-1"
                  >
                    🏆 WINNERS ONLY
                  </button>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#141415]">
                    <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                      <th className="text-left px-4 py-2 font-normal">SYMBOL</th>
                      <th className="text-right px-4 py-2 font-normal">TRADES</th>
                      <th className="text-right px-4 py-2 font-normal">WIN %</th>
                      <th className="text-right px-4 py-2 font-normal">TOTAL %</th>
                      <th className="text-right px-4 py-2 font-normal w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(result.symbol_stats || {})
                      .sort(([, a], [, b]) => b.total_pnl_pct - a.total_pnl_pct)
                      .map(([sym, s]) => {
                        const isBad = s.total_pnl_pct < 0;
                        return (
                          <tr
                            key={sym}
                            className={`border-b border-[#27272A] group ${isBad ? "bg-[#FF3B30]/5" : ""}`}
                            data-testid={`bt-sym-${sym}`}
                          >
                            <td className="px-4 py-2 mono font-bold">{sym}</td>
                            <td className="px-4 py-2 mono text-right">{s.trades}</td>
                            <td className="px-4 py-2 mono text-right">{num(s.win_rate, 1)}%</td>
                            <td className={`px-4 py-2 mono text-right ${s.total_pnl_pct >= 0 ? "gain" : "loss"}`}>
                              {s.total_pnl_pct >= 0 ? "+" : ""}{num(s.total_pnl_pct)}%
                            </td>
                            <td className="px-2 py-2 text-right">
                              <button
                                onClick={() => removeOne(sym)}
                                data-testid={`bt-remove-${sym}`}
                                title={`Remove ${sym} from bot watchlist`}
                                className="opacity-0 group-hover:opacity-100 hover:text-[#FF3B30] text-[#71717A] transition-opacity"
                              >
                                <Trash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    {Object.keys(result.symbol_stats || {}).length === 0 && (
                      <tr><td colSpan={5} className="p-6 text-center text-[#71717A] mono text-xs">NO TRADES</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent trades */}
            <div className="panel" data-testid="bt-trades-panel">
              <div className="px-4 py-3 border-b border-[#27272A] mono text-[11px] tracking-[0.22em]">
                LAST {result.trades.length} TRADES
              </div>
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#141415]">
                    <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                      <th className="text-left px-4 py-2 font-normal">EXIT</th>
                      <th className="text-left px-4 py-2 font-normal">SYM</th>
                      <th className="text-left px-4 py-2 font-normal">SIDE</th>
                      <th className="text-right px-4 py-2 font-normal">P&L</th>
                      <th className="text-left px-4 py-2 font-normal">REASON</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.trades.slice().reverse().map((t, i) => (
                      <tr key={i} className="border-b border-[#27272A]">
                        <td className="px-4 py-2 mono text-xs text-[#A1A1AA]">
                          {new Date(t.exit_time).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </td>
                        <td className="px-4 py-2 mono font-bold">{t.symbol}</td>
                        <td className={`px-4 py-2 mono ${t.side === "long" ? "gain" : "loss"}`}>
                          {t.side === "long" ? <TrendingUp size={11} className="inline" /> : <TrendingDown size={11} className="inline" />}
                          {" "}{t.side.toUpperCase()}
                        </td>
                        <td className={`px-4 py-2 mono text-right ${t.pnl_pct >= 0 ? "gain" : "loss"}`}>
                          {t.pnl_pct >= 0 ? "+" : ""}{num(t.pnl_pct)}%
                        </td>
                        <td className="px-4 py-2 mono text-xs text-[#71717A]">{t.reason?.toUpperCase()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Params used */}
          <div className="panel" data-testid="bt-params">
            <div className="px-4 py-3 border-b border-[#27272A] mono text-[11px] tracking-[0.22em]">PARAMETERS USED</div>
            <div className="p-4 grid grid-cols-2 lg:grid-cols-7 gap-4 mono text-xs">
              {Object.entries(result.params_used).map(([k, v]) => (
                <div key={k}>
                  <div className="text-[#71717A] tracking-widest">{k.replace(/_/g, " ").toUpperCase()}</div>
                  <div className="text-white mt-1">{String(v)}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
